import {DOCUMENT} from '@angular/common';
import {effect, inject, Injectable, signal} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {TranslateService} from '@ngx-translate/core';
import {LanguageService} from '@core/services/language.service';


export interface SeoPageConfig {
  readonly titleKey: string;
  readonly descriptionKey: string;
  readonly canonicalPath: string;
  readonly titleParams?: Readonly<Record<string, string | number>>;
  readonly descriptionParams?: Readonly<Record<string, string | number>>;
  readonly titleParamKeys?: Readonly<Record<string, string>>;
  readonly descriptionParamKeys?: Readonly<Record<string, string>>;
  readonly imagePath?: string;
  readonly robots?: string;
}

@Injectable({providedIn: 'root'})
export class SeoService {
  private readonly document = inject(DOCUMENT);
  private readonly languageService = inject(LanguageService);
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly pageConfig = signal<SeoPageConfig | null>(null);

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      const config = this.pageConfig();
      if (config) {
        this.apply(config);
      }
    });
  }

  update(config: SeoPageConfig): void {
    this.pageConfig.set(config);
  }

  private apply(config: SeoPageConfig): void {
    const pageTitle = this.translate.instant(
      config.titleKey,
      this.resolveParams(config.titleParams, config.titleParamKeys),
    ) as string;
    const description = this.translate.instant(
      config.descriptionKey,
      this.resolveParams(config.descriptionParams, config.descriptionParamKeys),
    ) as string;
    const imagePath = config.imagePath ?? '/images/decor/hero-intelligence-sphere.svg';

    this.title.setTitle(pageTitle);
    this.updateMeta('name', 'description', description);
    this.updateMeta('name', 'robots', config.robots ?? 'index,follow');
    this.updateMeta('property', 'og:title', pageTitle);
    this.updateMeta('property', 'og:description', description);
    this.updateMeta('property', 'og:type', 'website');
    this.updateMeta('property', 'og:image', imagePath);
    this.updateMeta('name', 'twitter:card', 'summary_large_image');
    this.updateMeta('name', 'twitter:title', pageTitle);
    this.updateMeta('name', 'twitter:description', description);
    this.updateMeta('name', 'twitter:image', imagePath);
    this.updateCanonical(config.canonicalPath);
  }


  private resolveParams(
    values?: Readonly<Record<string, string | number>>,
    translationKeys?: Readonly<Record<string, string>>,
  ): Readonly<Record<string, string | number>> | undefined {
    if (!values && !translationKeys) {
      return undefined;
    }

    const translatedValues = Object.fromEntries(
      Object.entries(translationKeys ?? {}).map(([name, key]) => [
        name,
        this.translate.instant(key) as string,
      ]),
    );

    return {...values, ...translatedValues};
  }

  private updateMeta(attribute: 'name' | 'property', value: string, content: string): void {
    this.meta.updateTag({[attribute]: value, content});
  }

  private updateCanonical(path: string): void {
    const head = this.document.head;
    let canonical = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.rel = 'canonical';
      head.appendChild(canonical);
    }

    const origin = this.document.location?.origin ?? '';
    canonical.href = `${origin}${path}`;
  }
}
